import React, { useCallback, useEffect } from "react";
import {
  ActionIcon,
  Button,
  Group,
  Paper,
  Table,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconCheck,
  IconCirclePlus,
  IconX,
} from "@tabler/icons";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import ActionCellRender from "./actionCellRender";

const editActionCellStyle = {
  verticalAlign: "top",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const ActionIcons = ({ onSave, onCancel, t }) => (
  <Group spacing={4} noWrap position="right" sx={{ paddingTop: 2 }}>
    <Tooltip label={t("Save")} position="top" withArrow openDelay={500}>
      <ActionIcon size="sm" variant="subtle" color="teal" onClick={onSave}>
        <IconCheck size={16} stroke={1.5} />
      </ActionIcon>
    </Tooltip>
    <Tooltip label={t("Cancel")} position="top" withArrow openDelay={500}>
      <ActionIcon size="sm" variant="subtle" onClick={onCancel}>
        <IconX size={16} stroke={1.5} />
      </ActionIcon>
    </Tooltip>
  </Group>
);

const DraftAddRow = ({
  emptyDraft,
  validate,
  normalizeEntry,
  renderFields,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const form = useForm({
    initialValues: { ...emptyDraft },
  });

  const handleSave = useCallback(() => {
    const errors = validate?.(form.values);
    if (errors && Object.keys(errors).length > 0) {
      form.setErrors(errors);
      return;
    }
    onSave(normalizeEntry(form.values));
  }, [form, normalizeEntry, onSave, validate]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSave();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleSave, onCancel]);

  return (
    <tr>
      {renderFields(form)}
      <td style={editActionCellStyle}>
        <ActionIcons onSave={handleSave} onCancel={onCancel} t={t} />
      </td>
    </tr>
  );
};

const InlineContactList = ({
  rows = [],
  readOnly = false,
  onChange,
  columns = [],
  emptyDraft,
  validate,
  normalizeEntry,
  renderFields,
  btnLabel = "Add",
  actionsColumnWidth = 72,
  showColumnHeaders = false,
}) => {
  const { t } = useTranslation();
  const [editingIndex, setEditingIndex] = React.useState(null);
  const [addingDraftIds, setAddingDraftIds] = React.useState([]);

  const form = useForm({
    initialValues: emptyDraft,
  });

  const resetEditor = useCallback(() => {
    setEditingIndex(null);
    form.reset();
    form.clearErrors();
  }, [form]);

  const startAdd = useCallback(() => {
    setAddingDraftIds((ids) => [...ids, `${Date.now()}-${ids.length}`]);
  }, []);

  const cancelDraft = useCallback((draftId) => {
    setAddingDraftIds((ids) => ids.filter((id) => id !== draftId));
  }, []);

  const saveDraft = useCallback(
    (draftId, entry) => {
      onChange([entry, ...rows]);
      setAddingDraftIds((ids) => ids.filter((id) => id !== draftId));
    },
    [onChange, rows]
  );

  const startEdit = useCallback(
    (rowIndex) => {
      setEditingIndex(rowIndex);
      form.setValues({ ...rows[rowIndex] });
      form.clearErrors();
    },
    [form, rows]
  );

  const handleSaveEdit = useCallback(() => {
    if (editingIndex === null) return;

    const errors = validate?.(form.values);
    if (errors && Object.keys(errors).length > 0) {
      form.setErrors(errors);
      return;
    }

    const entry = normalizeEntry(form.values, rows[editingIndex]);
    const nextRows = [...rows];
    nextRows[editingIndex] = entry;

    resetEditor();
    onChange(nextRows);
  }, [
    editingIndex,
    form,
    normalizeEntry,
    onChange,
    resetEditor,
    rows,
    validate,
  ]);

  const handleDelete = useCallback(
    (rowIndex) => {
      const nextRows = [...rows];
      nextRows.splice(rowIndex, 1);
      if (editingIndex === rowIndex) {
        resetEditor();
      } else if (editingIndex !== null && editingIndex > rowIndex) {
        setEditingIndex(editingIndex - 1);
      }
      onChange(nextRows);
    },
    [editingIndex, onChange, resetEditor, rows]
  );

  const handleActionBtnClick = useCallback(
    ({ action, rowIndex }) => {
      if (action === "delete") return handleDelete(rowIndex);
      if (action === "edit") return startEdit(rowIndex);
    },
    [handleDelete, startEdit]
  );

  useEffect(() => {
    if (editingIndex === null) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSaveEdit();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        resetEditor();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editingIndex, handleSaveEdit, resetEditor]);

  const viewColumns = columns.filter((col) => !col.isAction);
  const actionColumn = columns.find((col) => col.isAction);

  const hasDraftRows = addingDraftIds.length > 0;
  const hasRows = rows.length > 0;
  const isEditingRow = editingIndex !== null;

  return (
    <>
      {(hasRows || hasDraftRows || isEditingRow) && (
        <Paper p={0} sx={{ overflowX: "auto" }}>
          <Table
            verticalSpacing="sm"
            horizontalSpacing="sm"
            sx={{
              "& table": {
                tableLayout: "fixed",
                width: "100%",
              },
            }}
          >
            <colgroup>
              {viewColumns.map((col) => (
                <col
                  key={col.field}
                  style={{
                    ...(col.width ? { width: col.width } : undefined),
                    ...(col.fill ? { width: "auto" } : undefined),
                    ...(col.minWidth ? { minWidth: col.minWidth } : undefined),
                  }}
                />
              ))}
              {!readOnly && actionColumn && (
                <col style={{ width: actionsColumnWidth }} />
              )}
            </colgroup>
            {showColumnHeaders && (
              <thead>
                <tr>
                  {viewColumns.map((col) => (
                    <th
                      key={col.field}
                      style={{
                        ...(col.width ? { width: col.width } : undefined),
                        ...(col.fill ? { width: "auto" } : undefined),
                        ...(col.minWidth ? { minWidth: col.minWidth } : undefined),
                      }}
                    >
                      {t(col.headerName ?? _.capitalize(col.field))}
                    </th>
                  ))}
                  {!readOnly && actionColumn && (
                    <th style={{ width: actionsColumnWidth, textAlign: "right" }} />
                  )}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, rowIndex) => {
                if (editingIndex === rowIndex) {
                  return (
                    <tr key={rowIndex}>
                      {renderFields(form)}
                      <td style={editActionCellStyle}>
                        <ActionIcons
                          onSave={handleSaveEdit}
                          onCancel={resetEditor}
                          t={t}
                        />
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={rowIndex}
                    onDoubleClick={
                      readOnly ? undefined : () => startEdit(rowIndex)
                    }
                    style={readOnly ? undefined : { cursor: "pointer" }}
                  >
                    {viewColumns.map((col) => (
                      <td
                        key={col.field}
                        style={{
                          verticalAlign: "top",
                          ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                        }}
                      >
                        {col.cellRender
                          ? col.cellRender(col, row)
                          : _.get(row, col.field)}
                      </td>
                    ))}
                    {!readOnly && actionColumn && (
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {ActionCellRender(
                          actionColumn,
                          rowIndex,
                          row,
                          handleActionBtnClick
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}

              {addingDraftIds.map((draftId) => (
                <DraftAddRow
                  key={draftId}
                  emptyDraft={emptyDraft}
                  validate={validate}
                  normalizeEntry={normalizeEntry}
                  renderFields={renderFields}
                  onSave={(entry) => saveDraft(draftId, entry)}
                  onCancel={() => cancelDraft(draftId)}
                />
              ))}
            </tbody>
          </Table>
        </Paper>
      )}

      {!readOnly && (
        <Button
          size="xs"
          variant="light"
          leftIcon={<IconCirclePlus size={14} />}
          onClick={startAdd}
          mt="sm"
        >
          {t(btnLabel)}
        </Button>
      )}
    </>
  );
};

export default InlineContactList;
